import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, Box, TextField, Button, InputAdornment, Menu, MenuItem,
} from '@mui/material';
import { Search as SearchIcon, FileDownload as ExportIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';

interface Column {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  format?: (value: any, row: any) => React.ReactNode;
  sx?: any;
}

interface PaginatedTableProps {
  columns: Column[];
  rows: any[];
  rowKey?: string;
  defaultRowsPerPage?: number;
  searchable?: boolean;
  searchFields?: string[];
  exportable?: boolean;
  exportFileName?: string;
  emptyMessage?: string;
  onRowClick?: (row: any) => void;
  renderActions?: (row: any) => React.ReactNode;
}

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  columns,
  rows,
  rowKey = 'id',
  defaultRowsPerPage = 10,
  searchable = true,
  searchFields,
  exportable = true,
  exportFileName = 'export',
  emptyMessage = 'No hay datos',
  onRowClick,
  renderActions,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [search, setSearch] = useState('');
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    const fields = searchFields || columns.map(c => c.id);
    return rows.filter(row =>
      fields.some(field => {
        const val = row[field];
        if (val == null) return false;
        return String(val).toLowerCase().includes(term);
      })
    );
  }, [rows, search, searchFields, columns]);

  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getExportData = () => {
    return filteredRows.map(row => {
      const obj: Record<string, any> = {};
      columns.forEach(col => {
        obj[col.label] = row[col.id] ?? '';
      });
      return obj;
    });
  };

  const handleExportExcel = () => {
    const data = getExportData();
    const ws = XLSX.utils.json_to_sheet(data);

    // Auto-width columns
    const colWidths = columns.map(col => {
      const maxLen = Math.max(
        col.label.length,
        ...filteredRows.map(r => String(r[col.id] ?? '').length)
      );
      return { wch: Math.min(maxLen + 2, 40) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, `${exportFileName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportAnchor(null);
  };

  const handleExportCSV = () => {
    const headers = columns.map(c => c.label).join(',');
    const csvRows = filteredRows.map(row =>
      columns.map(col => {
        const val = row[col.id];
        if (val == null) return '';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      }).join(',')
    );
    const csv = [headers, ...csvRows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${exportFileName}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  };

  return (
    <Paper>
      {(searchable || exportable) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, gap: 1 }}>
          {searchable ? (
            <TextField
              size="small"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
          ) : <Box />}
          {exportable && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                Exportar
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem onClick={handleExportExcel}>Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportCSV}>CSV (.csv)</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 700, minWidth: col.minWidth, ...col.sx }}
                >
                  {col.label}
                </TableCell>
              ))}
              {renderActions && <TableCell align="center" sx={{ fontWeight: 700 }}>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (renderActions ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow
                  key={row[rowKey]}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align || 'left'}>
                      {col.format ? col.format(row[col.id], row) : (row[col.id] ?? '-')}
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell align="center">{renderActions(row)}</TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Filas por pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Paper>
  );
};

export default PaginatedTable;
